import React from "react";
import { Globe } from "lucide-react";

// Map region label (as emitted by our backend sources) → ISO-3166-1 alpha-2
// country code served by flagcdn.com. We lower-case the key for a tolerant
// match and strip whitespace so e.g. "US-East" and "us-east" both work.
//
// For continent-wide buckets (Europe, Asia, Oceania, World) we return a
// pseudo-code and let the component render a Lucide Globe icon or the
// stylised EU flag as appropriate.
const REGION_TO_CODE = {
  // North America — treat the whole NA bucket as USA for the flag.
  "us-east": "us", "us-west": "us", "us-central": "us",
  "na-east": "us", "na-west": "us", "na-central": "us", "na-north": "us",
  "america-east": "us", "america-west": "us", "america-central": "us",
  "america-north": "us", "america-south": "us",

  // South America
  "sa-brazil": "br", "brazil": "br", "south-america": "br",

  // Europe — single EU block uses the EU flag; sub-regions use country flags
  // when there's a canonical choice.
  "europe": "eu", "eu-west": "eu", "eu-east": "eu", "eu-south": "eu",
  "eu-central": "eu", "europe-central": "eu",

  // Asia
  "asia": "cn", "asia-east": "cn",
  "asia-se": "sg", "asia-south-east": "sg",
  "asia-west": "ae", "middle-east": "ae", "middle east": "ae",
  "asia-central": "kz",
  "japan": "jp",

  // Oceania
  "oceania": "au", "australia": "au", "oce-sydney": "au",

  // Africa
  "africa": "za",
};

const UNKNOWN_KEYS = new Set(["world", "unknown", "unspecified", "—", "-"]);

const FLAG_CDN = "https://flagcdn.com";

export const RegionFlag = ({ region, size = 14, className = "", testId }) => {
  const key = (region || "").trim().toLowerCase();
  if (!key || UNKNOWN_KEYS.has(key)) {
    return (
      <Globe
        className={className}
        style={{ width: size, height: size, color: "var(--text-dim)" }}
        data-testid={testId || `region-flag-globe`}
      />
    );
  }
  const code = REGION_TO_CODE[key];
  if (!code) {
    return (
      <Globe
        className={className}
        style={{ width: size, height: size, color: "var(--text-dim)" }}
        data-testid={testId || `region-flag-globe`}
      />
    );
  }
  // EU uses a dedicated flag file at flagcdn (code `eu`).
  const src = `${FLAG_CDN}/${code}.svg`;
  return (
    <img
      src={src}
      alt={`${region} flag`}
      loading="lazy"
      className={`inline-block object-cover ${className}`}
      style={{
        width: size,
        height: Math.round(size * 0.75),
        borderRadius: 1,
        boxShadow: "0 0 0 1px color-mix(in srgb, var(--border-color) 60%, transparent)",
      }}
      data-testid={testId || `region-flag-${code}`}
    />
  );
};

export default RegionFlag;
